import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api } from '../lib/api'
import { AvailabilityLabel } from '../lib/labels'
import Loading from '../ui/Loading'

export default function AvailabilityAdminPage() {
    const { user, hasAnyRole } = useAuth()
    const [orgId, setOrgId] = useState('') // '' = 전체 조직
    const [status, setStatus] = useState('') // '' = 전체 상태
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [orgOptions, setOrgOptions] = useState([])

    useEffect(() => {
        let ignore = false
        async function loadOrgs() {
            try {
                // 로그인 사용자의 조직 + 하위 조직만 조회
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) {
                    const list = Array.isArray(data) ? data : []
                    setOrgOptions(list)
                    // 기본값: 내 조직이 옵션에 있으면 자동 선택
                    if (!orgId && user?.orgId) {
                        const exists = list.find(o => String(o.id) === String(user.orgId))
                        if (exists) {
                            setOrgId(String(user.orgId))
                        }
                    }
                }
            } catch { if (!ignore) setOrgOptions([]) }
        }
        loadOrgs()
        return () => { ignore = true }
    }, [orgId, user])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const params = {}
            if (orgId) params.orgId = Number(orgId)
            if (status) params.status = status
            const res = await api.get('/manager-status/search', { params })
            const data = res?.data?.data ?? res?.data ?? []
            setItems(Array.isArray(data) ? data : [])
        } finally {
            setLoading(false)
        }
    }, [orgId, status])

    useEffect(() => { load() }, [load])

    const endOne = async (id) => {
        try {
            await api.patch(`/manager-status/${id}`, { endAt: new Date().toISOString() })
            load()
        } catch { }
    }

    const deleteOne = async (id) => {
        if (!window.confirm('이 기록을 삭제하시겠습니까?')) return
        try {
            await api.delete(`/manager-status/${id}`)
            load()
        } catch { }
    }

    const allowedRoles = [
        Roles.OPERATOR,
        Roles.GA_LEADER,
        Roles.GA_BRANCH_MANAGER,
        Roles.GA_DEPUTY_BRANCH_MANAGER,
        Roles.INSURER_LEADER,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER
    ]

    if (!(hasAnyRole && hasAnyRole(allowedRoles))) {
        return <div className="text-sm text-slate-600">권한이 없습니다.</div>
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">근무 상태 관리</h1>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <label className="block text-sm text-slate-600">조직</label>
                    <select className="mt-1 w-full rounded border px-3 py-2" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                        <option value="">전체</option>
                        {orgOptions.map(o => (
                            <option key={o.id} value={o.id}>{o.name} ({o.type})</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm text-slate-600">상태</label>
                    <select className="mt-1 w-full rounded border px-3 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">전체</option>
                        <option value="ON_DUTY">근무중</option>
                        <option value="VACATION">휴가</option>
                        <option value="TRAINING">교육</option>
                        <option value="AWAY">부재중</option>
                    </select>
                </div>
                <div className="flex items-end">
                    <button className="rounded border px-3 py-2" onClick={load} disabled={loading}>{loading ? '불러오는 중…' : '검색'}</button>
                </div>
            </div>

            <div className="rounded-lg border mt-6 overflow-x-auto">
                {loading ? (
                    <Loading />
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="px-4 py-2 text-left">사용자</th>
                                <th className="px-4 py-2 text-left">상태</th>
                                <th className="px-4 py-2 text-left">기간</th>
                                <th className="px-4 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(it => (
                                <tr key={it.id} className="border-t hover:bg-slate-50">
                                    <td className="px-4 py-2">{it.userName} (#{it.userId})</td>
                                    <td className="px-4 py-2">{AvailabilityLabel[it.status] || it.status}</td>
                                    <td className="px-4 py-2">{it.startAt || '-'} → {it.endAt || (it.id ? '진행중' : '-')}</td>
                                    <td className="px-4 py-2 text-right">
                                        {!!it.id && !it.endAt && (
                                            <button className="text-brand-navy hover:underline mr-3" onClick={() => endOne(it.id)}>종료</button>
                                        )}
                                        {!!it.id && (
                                            <button className="text-red-600 hover:underline" onClick={() => deleteOne(it.id)}>삭제</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    )
}


